import { CSSProperties, useEffect, useState } from "react"
import InputSymphony from "elements/InputSymphony"
import ModalSymphony from "elements/ModalSymphony"
import TabsSymphony, { TabItem } from "elements/TabsSymphony"
import useCampaignBuilder from "Hooks/useCampaignBuilder"
import { PROMOTABLE_ITEMS, PromotableItem } from "modules/Campaign"
import { AdConnectionType, AdPlatform, PlaybookStepName } from "modules/Const"
import { clsx, makeKey, styx } from "modules/Utils"
import PlatformStartButton from "pages/MarketingPage/Components/CreateNewCampaignModal/PlatformStartButton"
import useFacebookConnections from "Hooks/useFacebookConnections"
import AdPreviewCampaignView, { getPreviewTabOptions } from "./CampaignViews/AdPreviewCampaignView"
import AdConnectionModal from "./shareable/AdConnectionModal"
import { useHistory } from "react-router-dom"
import useMaestroChatStore from "Hooks/useMaestroChat"
import TagSymphony from "elements/TagSymphony"
import Colors from "modules/Colors"
import Sty from "modules/Sty"
import useScreen from "Hooks/useScreen"
import useBrand from "Hooks/useBrand"
import useUser, { UserExistsState } from "Hooks/useUser"
import { track } from 'analytics';
import useOnboardingV2 from "Hooks/useOnboardingV2"

const MARKETING_TYPE_OPTIONS: TabItem[] = [
    {
        id: 'goals',
        title: 'Choose a Goal',
    },
    {
        id: 'ad-platforms',
        title: 'Choose an Ad Platform',
    }
]

const AD_PLATFORM_OPTIONS: {
    id: AdPlatform,
    title: string,
    icon: string,
    onlyForSymphonyBetaTestUsers?: boolean,
}[] = [
        {
            id: AdPlatform.META,
            title: 'Instagram Ads',
            icon: 'instagram',
        },
        {
            id: AdPlatform.TIKTOK,
            title: 'TikTok Ads',
            icon: 'tiktok'
        },
        {
            id: AdPlatform.PRESAVE,
            title: 'Presaves',
            icon: 'preSave',
        },
        {
            id: AdPlatform.YOUTUBE,
            title: 'YouTube Ads',
            icon: 'youtube',
        },
        {
            id: AdPlatform.PLAYLISTS,
            title: 'Playlists',
            icon: 'spotify',
        }
    ]

interface CreateNewCampaignModalV2Props {
    className?: string
    isModal?: boolean
    stylePlaybookContent?: CSSProperties
    height?: number
    isBorder?: boolean
    isOnboarding?: boolean
    onSelectCampaign?: () => void
}

// NOTE: component()
const CreateNewCampaignModalV2 = ({
    className,
    isModal = true,
    stylePlaybookContent,
    height = 292,
    isBorder = false,
    isOnboarding = false,
    onSelectCampaign = () => { },
}: CreateNewCampaignModalV2Props) => {
    const history = useHistory()
    const {
        mobileView,
        mStyle,
        cssCalc
    } = useScreen()
    const {
        currentBrand
    } = useBrand()
    const {
        isSymphonyBetaTestUser
    } = useUser() as UserExistsState
    const {
        setSelectedGoalUrl
    } = useOnboardingV2()
    const {
        showCreateNewCampaignModal,
        setShowCreateNewCampaignModal,
        generateOneClickCampaign,
        newCampaignAdPlatformFilter,
        setNewCampaignAdPlatformFilter
    } = useCampaignBuilder()

    const {
        isChatOpen,
        setIsChatOpen,
    } = useMaestroChatStore()

    const [selectedMarketingType, setSelectedMarketingType] = useState<TabItem>(MARKETING_TYPE_OPTIONS[0])

    const CONTENT_TABS = getPreviewTabOptions('facebook')
    const [selectedContentTab, setSelectedContentTab] = useState<TabItem>(CONTENT_TABS[0]);
    const [modalStep, setModalStep] = useState<PlaybookStepName>(PlaybookStepName.PROMOTABLE)
    const [searchQuery, setSearchQuery] = useState<string>("");

    const [showAdConnectionsModal, setShowAdConnectionsModal] = useState(false)
    const [selectedAdConnectionType, setSelectedAdConnectionType] = useState<AdConnectionType | null>(null)

    useEffect(() => {
        if (selectedAdConnectionType) {
            setShowAdConnectionsModal(true)
        } else {
            setShowAdConnectionsModal(false)
        }
    }, [selectedAdConnectionType])

    useEffect(() => {
        if (modalStep === PlaybookStepName.ONE_CLICK_CAMPAIGN_GENERATION) {
            generateOneClickCampaign()
        }
    }, [modalStep])

    const handleCloseModal = () => {
        setShowCreateNewCampaignModal(false)
        setModalStep(PlaybookStepName.PROMOTABLE)
        setNewCampaignAdPlatformFilter(AdPlatform.META)
    }

    if (!showCreateNewCampaignModal && isModal) {
        return null
    }

    const renderUrlEntryInput = () => {
        return (
            <InputSymphony
                className="runInstagramAdsSearchInput"
                placeholder="Enter a URL to promote"
                onChange={(value) => {
                    setSearchQuery(value)
                }}
                value={searchQuery}
            />
        )
    }

    const handlePromotableItemClick = (promotableItem: PromotableItem) => {
        setShowCreateNewCampaignModal(false)
        const {
            adPlatform,
            playbookType,
        } = promotableItem

        track('Playbook Selection', {
            adPlatform,
            playbookType,
            brand_id: currentBrand ? currentBrand.id : null,
            brand_slug: currentBrand ? currentBrand.slug : null,
            brand_name: currentBrand ? currentBrand.name : null,
            from: 'Create New Campaign Modal V2',
        })

        const campaignBuilderUrl = `/marketing2/new/${promotableItem.playbookType}`

        switch (promotableItem.adPlatform) {
            case AdPlatform.META:
                // switch (playbookTypeInfo.playbookType) {
                //     case PlaybookType.IG_FOLLOWERS:
                //         setCampaignGoal(GOAL_SELECTOR_GOAL_OPTIONS[CAMPAIGN_GOAL.BOOST_PROFILE_VISITS])
                //         break
                //     case PlaybookType.IG_POST:
                //         setCampaignGoal(GOAL_SELECTOR_GOAL_OPTIONS[CAMPAIGN_GOAL.BOOST_VIEWS])
                //         break
                //     default:
                //         break
                // }

                if (isOnboarding) {
                    setSelectedGoalUrl(campaignBuilderUrl)
                } else {
                    history.push(campaignBuilderUrl)
                }
                break
            case AdPlatform.TIKTOK:
                if (isOnboarding) {
                    setSelectedGoalUrl(campaignBuilderUrl)
                } else {
                    history.push(campaignBuilderUrl)
                }
                break
            case AdPlatform.YOUTUBE:
                if (isOnboarding) {
                    setSelectedGoalUrl('/marketing/new/video-views')
                } else {
                    history.push('/marketing/new/video-views')
                }
                break
            case AdPlatform.PLAYLISTS:
                if (isOnboarding) {
                    setSelectedGoalUrl('/marketing/new/playlists')
                } else {
                    history.push('/marketing/new/playlists')
                }
                break
            case AdPlatform.PRESAVE:
                if (isOnboarding) {
                    setSelectedGoalUrl('/marketing/new/presave')
                } else {
                    history.push('/marketing/new/presave')
                }
                break
        }

        onSelectCampaign()
    }

    const renderLeftSideContent = () => {
        return (
            <div className="oneClickCampaignModalLeftSideContent" style={sty.oneClickCampaignModalLeftSideContent}>
                <TabsSymphony
                    className="oneClickCampaignModalLeftSideContentTabs"
                    tabs={CONTENT_TABS}
                    selectedTab={selectedContentTab.id}
                    onChange={(tab) => setSelectedContentTab(tab as TabItem)}
                    style={{
                        backgroundColor: 'white',
                        width: 'fit-content',
                        borderRadius: 1000,
                        paddingLeft: 16,
                        paddingRight: 16,
                    }}
                />
                <div className="oneClickCampaignModalAdPreview" style={sty.oneClickCampaignModalAdPreview}>
                    <AdPreviewCampaignView
                        variant="facebook"
                        hidePlatformSelector={true}
                        hideTitle={true}
                        hideSubtitle={true}
                        overridePreviewType={selectedContentTab.id as 'ig-story' | 'ig-reels' | 'ig-feed'}
                    />
                </div>
            </div>
        )
    }

    const renderTabsContent = () => {
        let promotableItemsFiltered = [...PROMOTABLE_ITEMS]

        if (newCampaignAdPlatformFilter) {
            promotableItemsFiltered = PROMOTABLE_ITEMS.filter(
                (promotableItem) => promotableItem.adPlatform === newCampaignAdPlatformFilter
            )
        }

        promotableItemsFiltered = promotableItemsFiltered.filter(
            (promotableItem) => !promotableItem.unavailable
        )

        return (
            <div
                className="oneClickCampaignSetupModalPromotableItems1"
                style={styx(
                    mStyle(sty, 'oneClickCampaignSetupModalPromotableItems'),
                    { height: cssCalc(height) },
                    isBorder && { border: `1px solid ${Colors.greyLight}` },
                    !isModal && { borderRadius: 16, maxHeight: 540 },
                    stylePlaybookContent,
                )}
            >
                {promotableItemsFiltered.map((promotableItem, index) => {
                    const {
                        title,
                        icon,
                        description,
                        color,
                    } = promotableItem;

                    return (
                        <PlatformStartButton
                            key={makeKey('promotable-item', promotableItem.playbookType, index)}
                            className="platformStartButtonCreateNewCampaignModalV2"
                            onClick={() => handlePromotableItemClick(promotableItem)}
                            title={title}
                            description={description}
                            icon={icon}
                            fullWidth
                            size="large"
                            isBorder={false}
                            color={color}
                        />
                    )
                })}
            </div>
        )
    }

    const renderCreateNewCampaignContent = () => {
        const shownOptions = AD_PLATFORM_OPTIONS.filter((adPlatform) => {
            if (adPlatform.onlyForSymphonyBetaTestUsers) {
                return isSymphonyBetaTestUser
            }

            return true
        })

        return (
            <>
                <div
                    className="oneClickCampaignSetupModalAdPlatformTags"
                    style={{
                        paddingLeft: mobileView ? 8 : 16,
                        paddingRight: mobileView ? 8 : 16,
                        paddingBottom: 16,
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 8,
                        flexWrap: 'wrap',
                    }}
                >
                    {shownOptions.map((adPlatform, index) => {
                        const isSelected = newCampaignAdPlatformFilter === adPlatform.id
                        return (
                            <TagSymphony
                                key={makeKey('ad-platform', adPlatform.id, index)}
                                className="adPlatformTag"
                                style={{
                                    fontSize: 15,
                                    gap: 6
                                }}
                                iconSize={20}
                                title={adPlatform.title}
                                color={isSelected ? 'purple' : 'grey'}
                                onClick={() => setNewCampaignAdPlatformFilter(adPlatform.id as AdPlatform)}
                                icon={adPlatform.icon}
                            />
                        )
                    })}
                </div>

                {renderTabsContent()}
            </>
        )
    }

    // NOTE: render()
    return (
        <>
            {isModal ?
                <ModalSymphony
                    className={clsx('oneClickCampaignSetupModal', className)}
                    isOpen={showCreateNewCampaignModal}
                    onClose={handleCloseModal}
                    title="What do you want to promote?"
                    size="medium"
                    subtitle="Create an AI-powered ad campaign to grow anything."
                    isIsolateModalContent
                    fullScreen={mobileView}
                >
                    {renderCreateNewCampaignContent()}
                </ModalSymphony>
                :
                renderCreateNewCampaignContent()
            }

            {/* {selectedAdConnectionType &&
                <AdConnectionModal
                    className={clsx('advertisingConnectionModal', className)}
                    isOpen={showAdConnectionsModal}
                    onClose={() => setSelectedAdConnectionType(null)}
                    connectionType={selectedAdConnectionType}
                />
            } */}
        </>
    )
}

const sty: Record<string, CSSProperties> = {
    oneClickCampaignSetupModalPromotableItems: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: 16,
        backgroundColor: Colors.greyLight,
        boxShadow: Sty.boxShadowInset,
        overflowY: 'scroll',
        scrollbarWidth: 'none',
    },
    oneClickCampaignSetupModalPromotableItems_mobile: {
        padding: 8,
    },
    oneClickCampaignSetupModalContent: {
        marginBottom: 24,
        marginLeft: 'auto',
        marginRight: 'auto',
        width: '100%',
        padding: 16,
    },
    oneClickCampaignModalLeftSideContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, rgba(111, 66, 193, 0.9) 0%, rgba(72, 149, 239, 0.8) 100%)',
        height: '100%',
        padding: 24,
        boxShadow: 'inset 0px 0px 20px 0px rgba(0, 0, 0, 0.5)',
        gap: 24,
        borderBottomLeftRadius: 16,
        borderTopLeftRadius: 16,
    },
    oneClickCampaignModalAdPreview: {
        width: 280,
        height: 450,
        backgroundColor: 'white',
        borderRadius: 16,
        boxShadow: '0px 0px 20px 0px rgba(0, 0, 0, 0.5)',
    },
    oneClickCampaignSetupModalPresavesSelection: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    goalsCategoryTagsContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: 8,
        flexWrap: 'wrap',
    },
    oneClickCampaignSetupModalTabsContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 16,
    }
}

export default CreateNewCampaignModalV2